import { useMemo } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  BaseForm,
  FormInput,
  FormSelect,
  FormTextarea,
  FormToggle,
} from "components/BaseForm";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import { useCustomFieldConfigMutations } from "hooks/useCustomFieldConfigTable";
import usePageTitle from "hooks/usePageTitle";
import CustomFieldService from "services/CustomField";

const LIST_PATH = "/settings/custom_field_configs";

const MODEL_TYPE_OPTIONS = [
  { key: "Contact", value: "Contact", text: "Contact" },
  { key: "Entity", value: "Entity", text: "Entity" },
  { key: "Campaign", value: "Campaign", text: "Campaign" },
  { key: "CampaignTarget", value: "CampaignTarget", text: "Campaign Target" },
  {
    key: "CampaignTargetContact",
    value: "CampaignTargetContact",
    text: "Campaign Target Contact",
  },
  { key: "Contract", value: "Contract", text: "Contract" },
  { key: "Case", value: "Case", text: "Case" },
  {
    key: "RevenueOpportunity",
    value: "RevenueOpportunity",
    text: "Revenue Opportunity",
  },
  { key: "EmailTemplate", value: "EmailTemplate", text: "Email Template" },
  { key: "User", value: "User", text: "User" },
];

const FIELD_TYPE_OPTIONS = [
  { key: "text", value: "text", text: "Text" },
  { key: "numeric", value: "numeric", text: "Numeric" },
  { key: "boolean", value: "boolean", text: "Boolean" },
  { key: "select", value: "select", text: "Categorical" },
  { key: "date", value: "date", text: "Date" },
  { key: "list", value: "list", text: "List" },
  { key: "table", value: "table", text: "Table" },
];

const SELECT_TYPE_OPTIONS = [
  { key: "single", value: "single", text: "Single" },
  { key: "multi", value: "multi", text: "Multiple" },
];

const schema = z
  .object({
    required: z.boolean(),
    label: z.string().trim().min(1, "Label is required"),
    model_type: z.string().nullable().refine(Boolean, "Model Type is required"),
    field_type: z.string().nullable().refine(Boolean, "Field Type is required"),
    select_type: z.string().nullable(),
    options: z.string(),
    description: z.string().trim().min(1, "Description is required"),
  })
  .superRefine((values, ctx) => {
    if (values.field_type !== "select") return;
    if (!values.select_type) {
      ctx.addIssue({
        path: ["select_type"],
        code: z.ZodIssueCode.custom,
        message: "Categorical Select Type is required",
      });
    }
    if (!values.options.trim()) {
      ctx.addIssue({
        path: ["options"],
        code: z.ZodIssueCode.custom,
        message: "Field Options are required",
      });
    }
  });

// Categorical options round-trip through the form as a comma-separated string
// but are stored as option objects, so both directions need a conversion.
const optionsToCsv = options =>
  Array.isArray(options) ? options.map(o => o.value).join(", ") : "";

const csvToOptions = csv =>
  csv
    .split(",")
    .map(option => option.trim())
    .filter(Boolean)
    .map(option => ({
      id: option,
      value: option,
      text: option,
      name: option,
    }));

const TableFormatInstructions = () => (
  <div className="tw:text-(--color-text-secondary) tw:space-y-2 tw:text-sm">
    <p>
      Tables will be entered in the format:
      <br />
      <code>
        <i>Column1</i>, <i>Column2</i>, <i>Column3</i>; <i>Row1Value1</i>,{" "}
        <i>Row1Value2</i>, <i>Row1Value3</i>; <i>Row2Value1</i>,{" "}
        <i>Row2Value2</i>, <i>Row2Value3</i>
      </code>
    </p>
    <p>
      The first row defines the column headers, and subsequent rows define the
      data. Use semicolons (;) to separate rows and commas (,) to separate
      columns within each row.
    </p>
    <p>
      <strong className="tw:text-(--color-text-primary)">
        For values containing commas:
      </strong>{" "}
      Wrap them in double quotes:
      <br />
      <code>
        &quot;Department Name&quot;, &quot;Last Name, First Name&quot;,
        Position; HR, &quot;Doe, John&quot;, Manager
      </code>
    </p>
  </div>
);

/**
 * Only Categorical and Table fields carry extra configuration, so the card is
 * absent for every other field type rather than rendered empty.
 */
const FieldOptionsCard = () => {
  const fieldType = useWatch({ name: "field_type" });

  if (fieldType !== "select" && fieldType !== "table") return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {fieldType === "select" ? "Field Options" : "Table Format"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {fieldType === "select" ? (
          <div className="tw:flex tw:flex-col tw:gap-5">
            <FormSelect
              name="select_type"
              label="Categorical Select Type"
              required
              options={SELECT_TYPE_OPTIONS}
            />
            <FormTextarea
              name="options"
              label="Field Options"
              required
              placeholder="add your options as a comma separated value ex:(1,apple,blue)"
            />
          </div>
        ) : (
          <TableFormatInstructions />
        )}
      </CardContent>
    </Card>
  );
};

const CustomFieldConfig = ({ mode }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.customFieldConfig.edit
      : ACL_RELATIONSHIPS.customFieldConfig.create,
  ]);

  const { create, update } = useCustomFieldConfigMutations();

  const {
    data: config,
    isSuccess: configLoaded,
    isError: configError,
  } = useQuery({
    queryKey: ["custom-field-config", id],
    queryFn: () => CustomFieldService.getCustomFieldConfig(id),
    enabled: isEdit,
    retry: false,
  });

  const defaultValues = useMemo(
    () => ({
      required: config?.required ?? false,
      label: config?.label ?? "",
      model_type: config?.model_type ?? null,
      field_type: config?.field_type ?? null,
      select_type: config?.field_options?.type ?? null,
      options: optionsToCsv(config?.field_options?.options),
      description: config?.description ?? "",
    }),
    [config]
  );

  const pageTitle = isEdit ? "Edit Custom Field" : "New Custom Field";

  usePageTitle(isEdit ? config?.label : pageTitle);

  const handleSubmit = async values => {
    const data = {
      label: values.label.trim(),
      model_type: values.model_type,
      field_type: values.field_type,
      description: values.description.trim(),
      required: values.required,
      // Only categorical fields carry options; every other type stores an
      // empty config so switching away from Categorical clears stale choices.
      field_options:
        values.field_type === "select"
          ? { type: values.select_type, options: csvToOptions(values.options) }
          : {},
    };

    if (isEdit) {
      await update.mutateAsync({ id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  const handleSuccess = () => {
    toast.success(
      isEdit ? "Custom field config updated" : "Custom field config created"
    );
    navigate(LIST_PATH);
  };

  const handleCancel = () => navigate(LIST_PATH);

  if (!canWrite) {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (configError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this custom field. Please try again."}</p>
        <Link to={LIST_PATH}>Back to Custom Fields</Link>
      </div>
    );
  }

  if (isEdit && !configLoaded) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          pageTitle={pageTitle}
          backLink={{ to: LIST_PATH, label: "Back to Custom Fields" }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          submitButtonText="Save"
        >
          <Card>
            <CardHeader>
              <CardTitle>Field Definition</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:flex tw:flex-col tw:gap-5">
                <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                  <FormInput name="label" label="Label" required />
                  <FormSelect
                    name="model_type"
                    label="Model Type"
                    required
                    options={MODEL_TYPE_OPTIONS}
                    disabled={isEdit}
                  />
                  <FormSelect
                    name="field_type"
                    label="Field Type"
                    required
                    options={FIELD_TYPE_OPTIONS}
                  />
                  <FormToggle name="required" label="Required" />
                </div>
                <FormTextarea name="description" label="Description" required />
              </div>
            </CardContent>
          </Card>

          <FieldOptionsCard />
        </BaseForm>
      </div>
    </div>
  );
};

export default CustomFieldConfig;
